import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/string';
import '../../pdf/draw/transforms';

/** Scaled vector rotated by angleDeg, added to point p — port of Puzzle.Calc(p, v, V(scale, angle)...) */
function calc(
  p: PdfPoint,
  v: { x: number; y: number },
  ...steps: [scale: number, angleDeg: number][]
): PdfPoint {
  let px = p.x,
    py = p.y;
  for (const [scale, angleDeg] of steps) {
    const vx = v.x * scale;
    const vy = v.y * scale;
    const rad = (angleDeg * Math.PI) / 180;
    px += vx * Math.cos(rad) - vy * Math.sin(rad);
    py += vx * Math.sin(rad) + vy * Math.cos(rad);
  }
  return new PdfPoint(px, py);
}

/** Direction vector between two points — port of Puzzle.Vector() */
function vec(p0: PdfPoint, p1: PdfPoint): { x: number; y: number } {
  return { x: p1.x - p0.x, y: p1.y - p0.y };
}

/**
 * Trimino — Triangle-based puzzle matching worksheet.
 *
 * Layout: 2 pages.
 *   Page 1 (question): 9 interlocking triangles; each triangle edge shows
 *     either an image or its text label (alternating per item, random start).
 *   Page 2 (answer): same layout but every edge always shows the image.
 *
 * The 9 triangles tile into a 2×3 triangular grid. Each edge carries a
 * label (index 1–9; index 0 = blank boundary). Students cut out the
 * triangles and rearrange them so matching images meet at shared edges.
 *
 * Blue puzzle-connector "stripe" and "corner" decorations mark the edge
 * midpoints for easy cut-and-reassemble.
 *
 * Migrated from C# EduFrame.Img10.Generators.Trimino (uses Puzzle helpers)
 */
export class Trimino implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 9;
  }
  countMin(): number {
    return 9;
  }
  countMax(): number {
    return 9;
  }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const rnd = opts.data.random;

    const margin = PdfUnit.FromMillimeters(5);
    const size1 = (PdfSize.A4.width - 2 * margin) / 2;
    const size2 = (((PdfSize.A4.height - 2 * margin) / 3) * 2) / Math.sqrt(3);
    const minSize = Math.min(size1, size2);
    const sizeW = minSize;
    const sizeH = (minSize * Math.sqrt(3)) / 2;

    const imgW = PdfUnit.FromMillimeters(25);
    const imgH = PdfUnit.FromMillimeters(25);
    const txtW = PdfUnit.FromMillimeters(40);
    const txtH = PdfUnit.FromMillimeters(15);

    // Each item randomly starts with image or text on the question page
    const switches = Array.from({ length: 10 }, () => rnd.next(0, 2) === 0);

    const page1 = pdf.addPage(); // question
    const page2 = pdf.addPage(); // answer

    // ── Triangle drawing ─────────────────────────────────────────────────
    // indexes[i] = 1-based image index for edge i; 0 = blank (no label)
    const drawTriangle = (gx: number, gy: number, top: boolean, ...indexes: number[]) => {
      const p1 = new PdfPoint(margin + gx * sizeW, margin + gy * sizeH);
      const p2 = new PdfPoint(margin + (gx + 1) * sizeW, margin + gy * sizeH);

      let list: PdfPoint[];
      if (top) {
        const p3 = new PdfPoint(margin + (gx + 0.5) * sizeW, margin + (gy - 1) * sizeH);
        list = [p1, p2, p3];
      } else {
        const p3 = new PdfPoint(margin + (gx + 0.5) * sizeW, margin + (gy + 1) * sizeH);
        list = [p1, p3, p2];
      }

      const pts = [...list, list[0]]; // closed ring

      // Pass 1: blue puzzle-connector decorations (stripe along edge, corner tab)
      for (let i = 0; i < list.length; i++) {
        const v = vec(pts[i], pts[i + 1]);

        const stripe: PdfPoint[] = [
          pts[i],
          pts[i + 1],
          calc(pts[i + 1], v, [0.03, -120]),
          calc(pts[i], v, [0.03, -60]),
        ];
        page1.drawPolygon(null, PdfBrush.LightSkyBlue, stripe);
        page2.drawPolygon(null, PdfBrush.LightSkyBlue, stripe);

        const corner: PdfPoint[] = [
          pts[i],
          calc(pts[i], v, [0.15, 0]),
          calc(pts[i], v, [0.15, -60]),
        ];
        page1.drawPolygon(null, PdfBrush.LightSkyBlue, corner);
        page2.drawPolygon(null, PdfBrush.LightSkyBlue, corner);
      }

      // Pass 2: images / text labels and edge outlines
      for (let i = 0; i < list.length; i++) {
        const v = vec(pts[i], pts[i + 1]);
        const pp = calc(pts[i], v, [0.5, 0], [0.15, -90]);

        drawLabel(page1, page2, pp, v, indexes[i]);

        page1.drawLine(GUtil.basePen, pts[i], pts[i + 1]);
        page2.drawLine(GUtil.basePen, pts[i], pts[i + 1]);
      }
    };

    // ── Edge label helper ─────────────────────────────────────────────────
    const drawLabel = (
      q: PageGen,
      a: PageGen,
      p: PdfPoint,
      v: { x: number; y: number },
      idx: number
    ) => {
      if (idx === 0) return; // blank boundary edge
      const index = idx - 1;

      const angleDeg = (-Math.atan2(v.y, v.x) * 180) / Math.PI;
      const r1 = new PdfRect(p.x - imgW / 2, p.y - imgH / 2, imgW, imgH);
      const r2 = new PdfRect(p.x - txtW / 2, p.y - txtH / 2, txtW, txtH);

      // Answer page: always image
      a.save();
      a.rotateAtTransform(angleDeg, p);
      if (index < opts.list.length) {
        a.drawImageCenter(opts.list[index].image, r1);
      }
      a.restore();

      // Question page: image or text label, alternating per item
      q.save();
      q.rotateAtTransform(angleDeg, p);
      if (index < opts.list.length) {
        if (switches[index]) {
          q.drawImageCenter(opts.list[index].image, r1);
        } else {
          q.drawString(
            opts.list[index].label,
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r2,
            ETextAlign.Center
          );
        }
      }
      q.restore();

      switches[index] = !switches[index];
    };

    // ── 9 triangles forming the Trimino puzzle grid ───────────────────────
    drawTriangle(0, 0, false, 1, 0, 0);
    drawTriangle(0.5, 1, true, 4, 2, 0);
    drawTriangle(1, 0, false, 2, 3, 1);

    drawTriangle(0, 2, true, 3, 5, 0);
    drawTriangle(0.5, 1, false, 6, 0, 0);
    drawTriangle(1, 2, true, 6, 7, 4);

    drawTriangle(0, 2, false, 8, 7, 0);
    drawTriangle(0.5, 3, true, 5, 8, 9);
    drawTriangle(1, 2, false, 9, 0, 0);
  }
}
